import { ARE_YOU_SURE_YOU_WANT_TO_DELETE_PHOTO, PHOTO_HAS_BEEN_DELETED } from 'mk/autogenerated/translations/BlogsImageDetail.sagas.d393d752979ca5368a13306635d21742'
import {
    blogs_api_delete_image_url,
    blogs_api_photo_detail_url,
    blogs_api_rotate_image_url,
    blogs_api_save_image_title_url,
} from 'mk/urls/functions';
import { photoTitleEditFormName } from 'mk2/apps/blogs/components/PhotoTitleEditForm';
import {
    blogsImageDetailDeleteImageApi,
    blogsImageDetailFetchApi,
    blogsImageDetailRotateImageApi,
    blogsImageDetailSaveImageTitleApi,
    blogsImageDetailTitleEdit,
    BlogsImageDetailDeleteImageNormalizedResponse,
    BlogsImageDetailDeleteImageTriggerAction,
    BlogsImageDetailFetchNormalizedResponse,
    BlogsImageDetailFetchTriggerAction,
    BlogsImageDetailRotateImageNormalizedResponse,
    BlogsImageDetailRotateImageTriggerAction,
    BlogsImageDetailSaveImageTitleNormalizedResponse,
    BlogsImageDetailSaveImageTitleTriggerAction,
    BLOGS_IMAGE_DETAIL_DELETE_IMAGE_TRIGGER,
    BLOGS_IMAGE_DETAIL_FETCH_TRIGGER,
    BLOGS_IMAGE_DETAIL_ROTATE_IMAGE_TRIGGER,
    BLOGS_IMAGE_DETAIL_SAVE_IMAGE_TITLE_TRIGGER,
} from 'mk2/apps/blogs/containers/BlogsImageDetail/BlogsImageDetail.actions';
import { commentsFetchApi, CommentsFetchNormalizedResponse } from 'mk2/apps/blogs/containers/Comments/Comments.actions';
import { getCommentParent } from 'mk2/apps/blogs/helpers';
import { findBlogPost } from 'mk2/apps/blogs/selectors';
import {
    blogsAlbumDetailUrl,
    blogsAlbumPhotoUrl,
    blogsAutoMessagePhotoUrl,
    blogsAutoMessageUrl,
    blogsShortMessagePhotoUrl,
    blogsShortMessageUrl,
} from 'mk2/apps/blogs/urls';
import { ComplaintEntity, ComplaintSchema } from 'mk2/containers/Complaint/Complaint.schemas';
import {
    handleXHRGetErrorSaga,
    handleXHRPostErrorSaga,
    normalizeError,
    XHRAction,
    XHRFormError,
    XHRGetError,
    XHRPostError,
} from 'mk2/helpers/api';
import { handleXHRFormErrorSaga, handleXHRFormSuccessSaga } from 'mk2/helpers/form';
import { showSuccessToast } from 'mk2/helpers/toasts';
import { url } from 'mk2/helpers/urls';
import { getLogger } from 'mk2/logger';
import { AppState } from 'mk2/reducers';
import {
    BlogPostEntity,
    BlogPostSchema,
    BlogPostType,
    CommentEntity,
    CommentParent,
    CommentSchema,
    ImageEntity,
    ImageSchema,
} from 'mk2/schemas';
import { redirectInPWASaga } from 'mk2/services/browserHistory';
import { normalize } from 'normalizr';
import { all, call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';

const logger = getLogger('blogs/BlogsImageDetail.sagas');

interface ApiResponsePhotoDetail {
    body: {
        canAdd: boolean;
        comments: CommentEntity[];
        complaints: ComplaintEntity[];
        isTiedToPost: boolean;
        post: BlogPostEntity;
    };
}

function* fetchPhotoDetail({ username, postSlug, imageId, xhr }: BlogsImageDetailFetchTriggerAction & XHRAction) {
    yield put(blogsImageDetailFetchApi.request(username, postSlug, imageId));

    try {
        const response: ApiResponsePhotoDetail = yield call(
            () => xhr.get(blogs_api_photo_detail_url(), { username, postSlug, imageId }),
        );

        const normalizedResponse: BlogsImageDetailFetchNormalizedResponse = normalize({
            comments: response.body.comments,
            complaints: response.body.complaints,
            post: response.body.post,
        }, {
            comments: [CommentSchema],
            complaints: [ComplaintSchema],
            post: BlogPostSchema,
        });

        // integrate loaded comments into Comments container
        const commentParent: CommentParent = getCommentParent(
            response.body.post,
            response.body.isTiedToPost
                ? null
                : response.body.post.images.find((img) => img.id === imageId),
        );
        const commentsNormalizedResponse: CommentsFetchNormalizedResponse = {
            result: {
                comments: normalizedResponse.result.comments,
                canAdd: response.body.canAdd,
                hasHiddenText: false,
            },
            entities: normalizedResponse.entities,
        };
        yield put(commentsFetchApi.success(commentParent, commentsNormalizedResponse));

        yield put(blogsImageDetailFetchApi.success(
            username,
            postSlug,
            imageId,
            normalizedResponse,
            response.body.isTiedToPost,
            response.body.canAdd,
        ));

    } catch (error) {
        yield handleXHRGetErrorSaga(error as XHRGetError, logger);
        yield put(blogsImageDetailFetchApi.failure(username, postSlug, imageId, normalizeError(error)));
    }
}

interface ApiResponseImageRotate {
    body: {
        image: ImageEntity;
    };
}

function* rotateImage({ username, postSlug, imageId, angle, xhr }: BlogsImageDetailRotateImageTriggerAction & XHRAction) {
    yield put(blogsImageDetailRotateImageApi.request(username, postSlug, imageId, angle));

    try {
        const response: ApiResponseImageRotate = yield call(
            () => xhr.post(blogs_api_rotate_image_url(imageId), { angle }),
        );

        const normalizedResponse: BlogsImageDetailRotateImageNormalizedResponse = normalize({
            image: response.body.image,
        }, {
            image: ImageSchema,
        });

        yield put(blogsImageDetailRotateImageApi.success(username, postSlug, imageId, angle, normalizedResponse));

    } catch (error) {
        yield handleXHRPostErrorSaga(error as XHRPostError, logger);
        yield put(blogsImageDetailRotateImageApi.failure(username, postSlug, imageId, angle, normalizeError(error)));
    }
}

interface ApiResponseImageDelete {
    body: {
        post: BlogPostEntity;
    };
}

function* deleteImage({ imageId, postSlug, username, xhr }: BlogsImageDetailDeleteImageTriggerAction & XHRAction) {
    if (!window.confirm(ARE_YOU_SURE_YOU_WANT_TO_DELETE_PHOTO)) {
        return;
    }

    const post: BlogPostEntity = yield select((state: AppState) => findBlogPost(state, postSlug));

    const numImages = post.images.length;
    const currentIndex = post.images.findIndex((image) => image.id === imageId);
    const newIdx = (currentIndex < numImages - 1) ? currentIndex + 1 : 0;
    const newImageId = post.images[newIdx].id;

    yield put(blogsImageDetailDeleteImageApi.request(username, postSlug, imageId));

    try {
        const response: ApiResponseImageDelete = yield call(
            () => xhr.post(blogs_api_delete_image_url(imageId), {}),
        );

        const normalizedResponse: BlogsImageDetailDeleteImageNormalizedResponse = normalize({
            post: response.body.post,
        }, {
            post: BlogPostSchema,
        });

        yield put(blogsImageDetailDeleteImageApi.success(username, postSlug, imageId, normalizedResponse));

        const gotoUrl = response.body.post.type === BlogPostType.ALBUM ? (
            numImages > 1
                ? url(blogsAlbumPhotoUrl, { username, postSlug, imageId: newImageId })
                : url(blogsAlbumDetailUrl, { username, postSlug })

        ) : response.body.post.type === BlogPostType.AUTOMATIC_MESSAGE ? (
            numImages > 1
                ? url(blogsAutoMessagePhotoUrl, { username, postSlug, imageId: newImageId })
                : url(blogsAutoMessageUrl, { username, postSlug })

        ) : (
            numImages > 1
                ? url(blogsShortMessagePhotoUrl, { username, postSlug, imageId: newImageId })
                : url(blogsShortMessageUrl, { username, postSlug })

        );

        yield redirectInPWASaga(gotoUrl);
        yield showSuccessToast(PHOTO_HAS_BEEN_DELETED);

    } catch (error) {
        yield handleXHRPostErrorSaga(error as XHRPostError, logger);
        yield put(blogsImageDetailDeleteImageApi.failure(username, postSlug, imageId, normalizeError(error)));
    }
}

interface ApiResponseImageSaveTitle {
    body: {
        image: ImageEntity;
    };
}

function* saveImageTitle({ username, postSlug, imageId, title, xhr }: BlogsImageDetailSaveImageTitleTriggerAction & XHRAction) {
    yield put(blogsImageDetailSaveImageTitleApi.request(username, postSlug, imageId, title));

    try {
        const response: ApiResponseImageSaveTitle = yield call(
            () => xhr.post(blogs_api_save_image_title_url(imageId), { title }),
        );

        const normalizedResponse: BlogsImageDetailSaveImageTitleNormalizedResponse = normalize({
            image: response.body.image,
        }, {
            image: ImageSchema,
        });

        yield handleXHRFormSuccessSaga(photoTitleEditFormName(imageId));
        yield put(blogsImageDetailTitleEdit(username, postSlug, imageId, false));
        yield put(blogsImageDetailSaveImageTitleApi.success(username, postSlug, imageId, title, normalizedResponse));

    } catch (error) {
        yield handleXHRFormErrorSaga(photoTitleEditFormName(imageId), error as XHRFormError, logger);
        yield put(blogsImageDetailTitleEdit(username, postSlug, imageId, false));
        yield put(blogsImageDetailSaveImageTitleApi.failure(username, postSlug, imageId, title, normalizeError(error)));
    }
}

export default function* root() {
    yield all([
        takeLatest(BLOGS_IMAGE_DETAIL_FETCH_TRIGGER, fetchPhotoDetail),
        takeEvery(BLOGS_IMAGE_DETAIL_ROTATE_IMAGE_TRIGGER, rotateImage),
        takeEvery(BLOGS_IMAGE_DETAIL_DELETE_IMAGE_TRIGGER, deleteImage),
        takeEvery(BLOGS_IMAGE_DETAIL_SAVE_IMAGE_TITLE_TRIGGER, saveImageTitle),
    ]);
}
